import { useState, useEffect } from 'react'
import BadgeBlock from '../dashboard/BadgeBlock'
import { supabase } from '../../lib/supabase'


// ── Jadwal ────────────────────────────────────────────────────
const getSessionToday = () => {
  const day = new Date().getDay()
  const sessions = {
    0: { label: 'Long Ride',      icon: '🗺️', color: '#60a5fa', desc: 'Flat / Rolling+Tanjakan' },
    1: { label: 'MTB / XC',       icon: '🚵', color: '#34d399', desc: 'Cross Country' },
    2: { label: 'Road Passing',   icon: '⚡', color: '#facc15', desc: 'Passing Motor' },
    3: { label: 'Road Passing',   icon: '⚡', color: '#facc15', desc: 'Passing Motor' },
    4: { label: 'MTB / XC',       icon: '🚵', color: '#34d399', desc: 'Cross Country' },
    5: { label: 'Indoor Trainer', icon: '🎯', color: '#a78bfa', desc: 'Roller / Trainer' },
    6: { label: 'Hill Repeat',    icon: '🏔️', color: '#f87171', desc: '2 Sesi, Rep sesuai coach' },
  }
  return sessions[day]
}

const getWeekSchedule = () => [
  { day: 'Sen', label: 'MTB/XC',    icon: '🚵', color: '#34d399' },
  { day: 'Sel', label: 'Road',      icon: '⚡', color: '#facc15' },
  { day: 'Rab', label: 'Road',      icon: '⚡', color: '#facc15' },
  { day: 'Kam', label: 'MTB/XC',    icon: '🚵', color: '#34d399' },
  { day: 'Jum', label: 'Indoor',    icon: '🎯', color: '#a78bfa' },
  { day: 'Sab', label: 'Hill',      icon: '🏔️', color: '#f87171' },
  { day: 'Min', label: 'Long Ride', icon: '🗺️', color: '#60a5fa' },
]

const getDayName = () => {
  const days = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu']
  return days[new Date().getDay()]
}

// ── Helper tanggal ────────────────────────────────────────────
const formatDate = (dateStr) => {
  if (!dateStr) return '—'
  const d = new Date(dateStr)
  return d.toLocaleDateString('id-ID', { day: 'numeric', month: 'short' })
}

const startOfWeek = () => {
  const d = new Date()
  const day = d.getDay()
  // Senin sebagai awal minggu
  const diff = day === 0 ? -6 : 1 - day
  d.setDate(d.getDate() + diff)
  d.setHours(0, 0, 0, 0)
  return d.toISOString()
}

// ── Hitung streak dari array tanggal sesi ────────────────────
const calcStreak = (sessionDates) => {
  if (!sessionDates.length) return 0
  const unique = [...new Set(sessionDates.map(d => d.split('T')[0]))].sort().reverse()
  let streak = 0
  let cursor = new Date()
  cursor.setHours(0, 0, 0, 0)
  for (const dateStr of unique) {
    const d = new Date(dateStr)
    const diff = Math.round((cursor - d) / 86400000)
    if (diff <= 1) {
      streak++
      cursor = d
    } else break
  }
  return streak
}

// ── Komponen utama ────────────────────────────────────────────
export default function Treemap({ profile, onBlockClick, athleteId: athleteIdProp }) {
  const [hovered, setHovered]   = useState(null)
  const [athleteId, setAthleteId] = useState(athleteIdProp || null)
  const [stats, setStats]       = useState(null)
  const [loading, setLoading]   = useState(true)

  const today    = getSessionToday()
  const schedule = getWeekSchedule()
  const todayIdx = new Date().getDay() === 0 ? 6 : new Date().getDay() - 1

  // ── Fetch athlete id jika belum ada ──────────────────────
  useEffect(() => {
    if (athleteIdProp || !profile?.id) return
    supabase
      .from('athletes')
      .select('id')
      .eq('profile_id', profile.id)
      .single()
      .then(({ data }) => { if (data) setAthleteId(data.id) })
  }, [profile?.id, athleteIdProp])

  // ── Fetch semua data sekaligus ────────────────────────────
  useEffect(() => {
    if (!athleteId) return

    const fetchStats = async () => {
      setLoading(true)
      try {
        // 1. Ambil 50 sesi terakhir (untuk streak, progress, last metrics)
        const { data: logs } = await supabase
          .from('session_logs')
          .select(`
            id, rpe, cadence, avg_speed, distance, heart_rate,
            created_at,
            sessions ( session_date, day_type )
          `)
          .eq('athlete_id', athleteId)
          .order('created_at', { ascending: false })
          .limit(50)

        if (!logs || logs.length === 0) {
          setStats(null)
          setLoading(false)
          return
        }

        // 2. Sesi minggu ini
        const weekStart = startOfWeek()
        const thisWeekLogs = logs.filter(l =>
          l.sessions?.session_date >= weekStart.split('T')[0]
        )
        const thisWeekCount = thisWeekLogs.length

        // 3. Total sesi (query count terpisah)
        const { count: totalSesi } = await supabase
          .from('session_logs')
          .select('id', { count: 'exact', head: true })
          .eq('athlete_id', athleteId)

        // 4. Streak
        const dates = logs
          .map(l => l.sessions?.session_date)
          .filter(Boolean)
        const streak = calcStreak(dates)

        // 5. Metrik sesi terakhir
        const last = logs[0]

        // 6. Hill repeat terakhir (day_type = 'hill_repeat')
        const lastHill = logs.find(l => l.sessions?.day_type === 'hill_repeat')
        let hillData = null
        if (lastHill) {
          const { data: hillReps } = await supabase
            .from('hill_repeat_logs')
            .select('session_number, rep_number')
            .eq('session_log_id', lastHill.id)
          if (hillReps?.length) {
            const s1 = hillReps.filter(r => r.session_number === 1).length
            const s2 = hillReps.filter(r => r.session_number === 2).length
            hillData = {
              sesi1: s1,
              sesi2: s2,
              lastDate: formatDate(lastHill.sessions?.session_date)
            }
          }
        }

        // 7. Long ride terakhir (day_type = 'long_ride')
        const lastLong = logs.find(l => l.sessions?.day_type === 'long_ride')
        let longData = null
        if (lastLong) {
          longData = {
            // mode long ride disimpan di sessions.mode: 'flat' atau 'rolling'
            type: lastLong.sessions?.mode === 'flat' ? 'Flat' : 'Rolling',
            distance: lastLong.distance ?? '—',
            lastDate: formatDate(lastLong.sessions?.session_date)
          }
        }

        setStats({
          progress: { thisWeek: thisWeekCount, totalSesi: totalSesi ?? 0, streak },
          rpe:      last.rpe ?? '—',
          cadence:  last.cadence ?? '—',
          avgSpeed: last.avg_speed ?? '—',
          distance: last.distance ?? '—',
          hr:       last.heart_rate ?? '—',
          hillRepeat: hillData,
          longRide:   longData,
          lastDate:   formatDate(last.sessions?.session_date),
        })
      } catch (err) {
        console.error('[Treemap] fetch error:', err)
        setStats(null)
      } finally {
        setLoading(false)
      }
    }

    fetchStats()
  }, [athleteId])

  // ── Shorthand untuk nilai (tampilkan '—' kalau null) ──────
  const v = (val, unit = '') =>
    val === null || val === undefined || val === '—'
      ? '—'
      : `${val}${unit ? '' : ''}` // unit ditampilkan terpisah di UI

  // ── Blok-blok treemap ─────────────────────────────────────
  const BLOCKS = [
    {
      id: 'today',
      size: 3,
      color: today.color,
      content: (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2.5rem' }}>{today.icon}</div>
          <div style={{ fontWeight: 'bold', fontSize: '1.1rem', marginTop: '0.5rem' }}>
            Sesi Hari Ini
          </div>
          <div style={{ fontSize: '1.3rem', fontWeight: 'bold', marginTop: '0.25rem' }}>
            {today.label}
          </div>
          <div style={{ fontSize: '0.8rem', opacity: 0.8, marginTop: '0.25rem' }}>
            {getDayName()} • {today.desc}
          </div>
        </div>
      )
    },
    {
      id: 'schedule',
      size: 2,
      color: '#a78bfa',
      content: (
        <div style={{ width: '100%' }}>
          <div style={{ fontWeight: 'bold', fontSize: '0.85rem', marginBottom: '0.5rem', textAlign: 'center' }}>
            📅 Jadwal Minggu Ini
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '2px' }}>
            {schedule.map((s, i) => (
              <div key={s.day} style={{
                flex: 1,
                textAlign: 'center',
                background: i === todayIdx ? s.color : `${s.color}33`,
                borderRadius: '6px',
                padding: '4px 2px',
                border: i === todayIdx ? `2px solid ${s.color}` : '2px solid transparent'
              }}>
                <div style={{ fontSize: '0.9rem' }}>{s.icon}</div>
                <div style={{ fontSize: '0.55rem', fontWeight: 'bold', color: i === todayIdx ? '#0f172a' : s.color }}>
                  {s.day}
                </div>
              </div>
            ))}
          </div>
        </div>
      )
    },
    {
      id: 'progress',
      size: 2,
      color: '#34d399',
      content: loading ? <LoadingDot color="#34d399" /> : (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2rem' }}>📈</div>
          <div style={{ fontWeight: 'bold', fontSize: '0.9rem', marginTop: '0.25rem' }}>Progress</div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '0.5rem' }}>
            <div>
              <div style={{ fontSize: '1.3rem', fontWeight: 'bold' }}>
                {stats?.progress.thisWeek ?? '—'}
              </div>
              <div style={{ fontSize: '0.65rem', opacity: 0.8 }}>Sesi minggu ini</div>
            </div>
            <div>
              <div style={{ fontSize: '1.3rem', fontWeight: 'bold' }}>
                {stats?.progress.streak ?? '—'}
              </div>
              <div style={{ fontSize: '0.65rem', opacity: 0.8 }}>Hari streak</div>
            </div>
            <div>
              <div style={{ fontSize: '1.3rem', fontWeight: 'bold' }}>
                {stats?.progress.totalSesi ?? '—'}
              </div>
              <div style={{ fontSize: '0.65rem', opacity: 0.8 }}>Total sesi</div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'rpe',
      size: 1,
      color: '#fb923c',
      content: loading ? <LoadingDot color="#fb923c" /> : (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem' }}>💪</div>
          <div style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>RPE Terakhir</div>
          <div style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>{v(stats?.rpe)}</div>
          <div style={{ fontSize: '0.6rem', opacity: 0.7 }}>
            {stats?.rpe !== '—' ? `dari 10 • ${stats?.lastDate}` : 'belum ada data'}
          </div>
        </div>
      )
    },
    {
      id: 'cadence',
      size: 1,
      color: '#f472b6',
      content: loading ? <LoadingDot color="#f472b6" /> : (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem' }}>⚙️</div>
          <div style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>Cadence</div>
          <div style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>{v(stats?.cadence)}</div>
          <div style={{ fontSize: '0.6rem', opacity: 0.7 }}>
            {stats?.cadence !== '—' ? 'rpm' : 'belum ada data'}
          </div>
        </div>
      )
    },
    {
      id: 'speed',
      size: 1,
      color: '#facc15',
      content: loading ? <LoadingDot color="#facc15" /> : (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem' }}>⚡</div>
          <div style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>Avg Speed</div>
          <div style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>{v(stats?.avgSpeed)}</div>
          <div style={{ fontSize: '0.6rem', opacity: 0.7 }}>
            {stats?.avgSpeed !== '—' ? 'km/jam' : 'belum ada data'}
          </div>
        </div>
      )
    },
    {
      id: 'distance',
      size: 1,
      color: '#4ade80',
      content: loading ? <LoadingDot color="#4ade80" /> : (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem' }}>📍</div>
          <div style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>Jarak</div>
          <div style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>{v(stats?.distance)}</div>
          <div style={{ fontSize: '0.6rem', opacity: 0.7 }}>
            {stats?.distance !== '—' ? 'km' : 'belum ada data'}
          </div>
        </div>
      )
    },
    {
      id: 'hr',
      size: 1,
      color: '#f43f5e',
      content: loading ? <LoadingDot color="#f43f5e" /> : (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem' }}>❤️</div>
          <div style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>Heart Rate</div>
          <div style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>{v(stats?.hr)}</div>
          <div style={{ fontSize: '0.6rem', opacity: 0.7 }}>
            {stats?.hr !== '—' ? 'bpm' : 'belum ada data'}
          </div>
        </div>
      )
    },
    {
      id: 'hillrepeat',
      size: 1,
      color: '#f87171',
      content: loading ? <LoadingDot color="#f87171" /> : (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem' }}>🏔️</div>
          <div style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>Hill Repeat</div>
          {stats?.hillRepeat ? (
            <>
              <div style={{ fontSize: '0.8rem', fontWeight: 'bold', marginTop: '0.25rem' }}>
                S1: {stats.hillRepeat.sesi1} rep
              </div>
              <div style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>
                S2: {stats.hillRepeat.sesi2} rep
              </div>
              <div style={{ fontSize: '0.6rem', opacity: 0.7 }}>{stats.hillRepeat.lastDate}</div>
            </>
          ) : (
            <div style={{ fontSize: '0.65rem', opacity: 0.7, marginTop: '0.25rem' }}>belum ada data</div>
          )}
        </div>
      )
    },
    {
      id: 'longride',
      size: 1,
      color: '#60a5fa',
      content: loading ? <LoadingDot color="#60a5fa" /> : (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem' }}>🗺️</div>
          <div style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>Long Ride</div>
          {stats?.longRide ? (
            <>
              <div style={{ fontSize: '0.9rem', fontWeight: 'bold', marginTop: '0.25rem' }}>
                {stats.longRide.type}
              </div>
              <div style={{ fontSize: '0.8rem' }}>{stats.longRide.distance} km</div>
              <div style={{ fontSize: '0.6rem', opacity: 0.7 }}>{stats.longRide.lastDate}</div>
            </>
          ) : (
            <div style={{ fontSize: '0.65rem', opacity: 0.7, marginTop: '0.25rem' }}>belum ada data</div>
          )}
        </div>
      )
    },
    {
      id: 'badges',
      size: 3,
      color: '#facc15',
      content: (
        <BadgeBlock athleteId={athleteId} onViewAll={() => {}} />
      )
    },
  ]

  const sizeMap = {
    3: { gridColumn: 'span 3', gridRow: 'span 2' },
    2: { gridColumn: 'span 2', gridRow: 'span 2' },
    1: { gridColumn: 'span 1', gridRow: 'span 1' },
  }

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(6, 1fr)',
      gridAutoRows: '120px',
      gap: '8px',
      padding: '8px',
      width: '100%',
      boxSizing: 'border-box'
    }}>
      {BLOCKS.map(block => (
        <div
          key={block.id}
          onClick={() => onBlockClick && onBlockClick(block.id)}
          onMouseEnter={() => setHovered(block.id)}
          onMouseLeave={() => setHovered(null)}
          style={{
            ...sizeMap[block.size],
            background: hovered === block.id
              ? `${block.color}44`
              : `${block.color}18`,
            border: `2px solid ${block.color}`,
            borderRadius: '12px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            padding: '0.75rem',
            boxSizing: 'border-box',
            color: block.color,
            overflow: 'hidden'
          }}
        >
          {block.content}
        </div>
      ))}
    </div>
  )
}

// ── Loading indicator kecil ───────────────────────────────────
function LoadingDot({ color }) {
  return (
    <div style={{
      width: 8, height: 8,
      borderRadius: '50%',
      background: color,
      opacity: 0.5,
      animation: 'pulse 1.2s ease-in-out infinite'
    }} />
  )
}

// inject animasi pulse sekali
if (typeof document !== 'undefined') {
  const id = 'treemap-pulse'
  if (!document.getElementById(id)) {
    const s = document.createElement('style')
    s.id = id
    s.textContent = `@keyframes pulse { 0%,100%{opacity:.3;transform:scale(1)} 50%{opacity:1;transform:scale(1.3)} }`
    document.head.appendChild(s)
  }
}